import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const AttendanceCalendar = () => {
  const { currentUser } = useAuth();
  const { attendance } = useData();
  const [currentDate, setCurrentDate] = useState(new Date());

  // Get current user ID
  const getCurrentUserId = () => {
    return currentUser?.id || currentUser?.uid;
  };

  // Filter attendance for current user
  const userAttendance = attendance.filter(record => 
    record.userId === getCurrentUserId() ||
    record.userId === currentUser?.id ||
    record.userId === currentUser?.uid
  );

  // Get calendar data
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  // Create calendar grid
  const calendarDays = [];
  
  // Add empty cells for days before the month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const attendanceRecord = userAttendance.find(record => record.date === dateStr);
    
    let status = 'absent'; // Default red
    let hoursWorked = 0;
    let details = 'No check-in';
    
    if (attendanceRecord && attendanceRecord.checkIn) {
      hoursWorked = attendanceRecord.hoursWorked || 0;
      
      if (hoursWorked >= 8) {
        status = 'full-day'; // Green
        details = `Full day (${hoursWorked}h)`;
      } else if (hoursWorked >= 4) {
        status = 'half-day'; // Yellow  
        details = `Half day (${hoursWorked}h)`;
      } else if (attendanceRecord.checkOut) {
        status = 'short-day'; // Light yellow
        details = `Short day (${hoursWorked}h)`;
      } else {
        status = 'working'; // Blue (currently working)
        details = `Working (checked in at ${attendanceRecord.checkIn})`;
      }
    }
    
    calendarDays.push({
      day,
      dateStr,
      status,
      hoursWorked,
      details,
      attendanceRecord
    });
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="attendance-calendar">
      <div className="calendar-header">
        <button 
          className="btn btn-outline btn-sm"
          onClick={() => navigateMonth(-1)}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        
        <h3>{monthNames[month]} {year}</h3>
        
        <button 
          className="btn btn-outline btn-sm"
          onClick={() => navigateMonth(1)}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color full-day"></div>
          <span className='projectName'>Full Day (8+ hours)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color half-day"></div>
          <span className='projectName'>Half Day (4-7 hours)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color short-day"></div>
          <span className='projectName'>Short Day (&lt;4 hours)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color working"></div>
          <span className='projectName'>Currently Working</span>
        </div>
        <div className="legend-item">
          <div className="legend-color absent"></div>
          <span className='projectName'>Absent</span>
        </div>
      </div>

      <div className="calendar-grid">
        {/* Day headers */}
        {dayNames.map(dayName => (
          <div key={dayName} className="calendar-day-header">
            {dayName}
          </div>
        ))}
        
        {/* Calendar days */}
        {calendarDays.map((dayData, index) => (
          <div 
            key={index} 
            className={`calendar-day ${dayData ? `status-${dayData.status}` : 'empty'}`}
            title={dayData ? dayData.details : ''}
          >
            {dayData && (
              <>
                <span className="day-number">{dayData.day}</span>
                {dayData.hoursWorked > 0 && (
                  <span className="hours-worked">{dayData.hoursWorked}h</span>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttendanceCalendar;
